import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { BookOpen, Film, Tv, Gamepad2, Play, Bookmark, Check, Pause, BarChart3, Pencil } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "../i18n";

const API = '/api';

const mediaTypeIcons = { book: BookOpen, movie: Film, tv_series: Tv, video_game: Gamepad2 };

export function Dashboard({ user }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, challengesRes, achievementsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/challenges`),
        axios.get(`${API}/achievements`),
        axios.post(`${API}/users/me/activity`).catch(() => {}),
      ]);
      setStats({
        ...statsRes.data,
        achievements_total: achievementsRes.data.total || 0,
        achievements_unlocked: achievementsRes.data.earned_count || 0,
        current_stage: achievementsRes.data.current_stage || null,
      });
      const activeChallenges = (challengesRes.data.challenges || []).filter(c => !c.completed);
      setChallenges(activeChallenges.slice(0, 3));
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error(t.dashboard.loadError);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-pulse text-muted-foreground">{t.dashboard.loadingDashboard}</div>
    </div>
  );

  const statusCounts = stats?.by_status || {};
  const totalItems = stats?.total_items || 0;
  const completedCount = statusCounts.completed || 0;
  const inProgressCount = statusCounts.in_progress || 0;
  const plannedCount = statusCounts.planned || 0;
  const droppedCount = statusCounts.dropped || 0;

  // Calculate achievement-based exploration progress
  const currentStage = stats?.current_stage;
  const stageProgress = currentStage ? (currentStage.earned / currentStage.total) * 100 : 0;
  const stageRemaining = currentStage ? currentStage.remaining : 0;
  const stageName = t.achievementStages?.[currentStage?.name] || currentStage?.name || t.achievementStages?.["Inicio"] || "Inicio";

  const firstName = user?.username || "";

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Greeting */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground" data-testid="dashboard-greeting">
          {t.dashboard.welcomeBack} {firstName}!
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t.dashboard.overview}
        </p>
      </motion.div>

      {/* Explora Section */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05, duration: 0.3 }}>
        <Card data-testid="explora-section">
          <CardContent className="p-4 md:p-5">
            <h2 className="text-sm font-semibold text-foreground mb-3">{t.dashboard.exploreSection}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ExploraCard
                icon={Play}
                iconColor="text-primary"
                iconBg="bg-primary/10"
                label={t.dashboard.currentlyInProgress}
                count={inProgressCount}
                link="/library?status=in_progress"
                testId="stat-in-progress"
                delay={0}
              />
              <ExploraCard
                icon={Bookmark}
                iconColor="text-yellow-500"
                iconBg="bg-yellow-500/10"
                label={t.dashboard.planned}
                count={plannedCount}
                link="/library?status=planned"
                testId="stat-planned"
                delay={0.05}
              />
              <ExploraCard
                icon={Check}
                iconColor="text-emerald-600"
                iconBg="bg-emerald-600/10"
                label={t.dashboard.completed}
                count={completedCount}
                link="/library?status=completed"
                testId="stat-completed"
                delay={0.1}
              />
              <ExploraCard
                icon={Pause}
                iconColor="text-red-500"
                iconBg="bg-red-500/10"
                label={t.dashboard.dropped}
                count={droppedCount}
                link="/library?status=dropped"
                testId="stat-dropped"
                delay={0.15}
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Conecta Section */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.3 }}>
        <Card data-testid="conecta-section">
          <CardContent className="p-4 md:p-5">
            <h2 className="text-sm font-semibold text-foreground mb-3">{t.dashboard.connectSection}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <ConectaButton icon={BarChart3} label={t.dashboard.latestActivity} link="/feed" testId="conecta-feed" />
              <ConectaButton icon={Pencil} label={t.dashboard.discover} link="/search" testId="conecta-discover" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Achievement Progress */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.3 }}>
        <Link to="/achievements" data-testid="achievement-progress-link">
        <Card data-testid="exploration-progress" className="cursor-pointer hover:bg-muted/30 transition-colors">
          <CardContent className="p-4 md:p-5">
            <h2 className="text-sm font-semibold text-foreground mb-1">{t.dashboard.achievementProgress}</h2>
            <p className="text-xs text-muted-foreground mb-3">{t.dashboard.youAreAt(stageName)}</p>
            <Progress value={stageProgress} className="h-2.5 mb-3" />
            <p className="text-xs text-muted-foreground">
              {stageRemaining > 0
                ? t.dashboard.achievementOnTrack(stageRemaining)
                : t.dashboard.achievementAllDone
              }
            </p>
          </CardContent>
        </Card>
        </Link>
      </motion.div>

      {/* Collection by type */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.3 }}>
        <Card data-testid="collection-by-type">
          <CardContent className="p-4 md:p-5">
            <h2 className="text-sm font-semibold text-foreground mb-3">{t.dashboard.collectionByType}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {["tv_series", "movie", "book", "video_game"].map((type, i) => {
                const Icon = mediaTypeIcons[type];
                const typeCount = stats?.by_media_type?.find(m => m._id === type)?.count || 0;
                return (
                  <Link key={type} to={`/library?type=${type}`} data-testid={`collection-${type}`}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.25 + i * 0.05 }}
                    >
                      <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border border-border bg-background hover:bg-muted/50 hover:border-foreground/10 transition-all cursor-pointer group">
                        <Icon className="w-6 h-6 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                        <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors font-medium">
                          {t.mediaTypesPlural[type] || type}
                        </span>
                        {typeCount > 0 && (
                          <span className="text-[10px] text-muted-foreground/70">{typeCount}</span>
                        )}
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Active challenges */}
      {challenges.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.3 }}>
          <Card data-testid="challenges-widget">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  {t.dashboard.activeGoals}
                </h2>
                <Link to="/challenges" className="text-xs text-primary hover:underline">{t.common.viewAll}</Link>
              </div>
              <div className="space-y-3">
                {challenges.map((challenge) => {
                  const progressPercent = challenge.target_count > 0
                    ? Math.min((challenge.progress / challenge.target_count) * 100, 100)
                    : 0;
                  return (
                    <div key={challenge.challenge_id} className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium truncate flex-1">{challenge.title}</span>
                        <span className="text-muted-foreground ml-2">{challenge.progress}/{challenge.target_count}</span>
                      </div>
                      <Progress value={progressPercent} className="h-1.5" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Empty state */}
      {totalItems === 0 && (
        <Card className="text-center py-10" data-testid="empty-dashboard">
          <CardContent>
            <BookOpen className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-base font-semibold mb-2">{t.dashboard.emptyTitle}</h3>
            <p className="text-sm text-muted-foreground mb-5">{t.dashboard.emptyDescription}</p>
            <Link
              to="/search"
              className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              {t.common.browsMedia}
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ExploraCard({ icon: Icon, iconColor, iconBg, label, count, link, testId, delay }) {
  return (
    <Link to={link} data-testid={testId}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.25 }}
      >
        <div className="flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border border-border bg-background hover:bg-muted/50 hover:border-foreground/10 transition-all cursor-pointer">
          <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={2} />
          </div>
          <span className="text-2xl font-bold text-foreground">{count}</span>
          <span className="text-[11px] text-muted-foreground text-center leading-tight">{label}</span>
        </div>
      </motion.div>
    </Link>
  );
}

function ConectaButton({ icon: Icon, label, link, testId }) {
  return (
    <Link to={link} data-testid={testId}>
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-border bg-background hover:bg-muted/50 hover:border-foreground/10 transition-all cursor-pointer">
        <Icon className="w-[18px] h-[18px] text-muted-foreground flex-shrink-0" strokeWidth={1.8} />
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
    </Link>
  );
}

export default Dashboard;
